import {
	DB_BOXES,
	DB_DOTS,
	DB_H_COUNT,
	DB_V_COUNT,
	type DotsBoxesState,
	type PlayerIndex,
} from "@playpal/core";

interface Props {
	state: DotsBoxesState;
	seat: PlayerIndex;
	onMove: (move: unknown) => void;
}

const GAP = 100;
const PAD = 34;
/** Thickness of the invisible tap target around each line. */
const HIT = 34;
const SIZE = PAD * 2 + (DB_DOTS - 1) * GAP;

const LINE_COLOR = ["stroke-rose-500", "stroke-sky-500"] as const;
const BOX_COLOR = ["fill-rose-400/70", "fill-sky-400/70"] as const;

const dotX = (col: number) => PAD + col * GAP;
const dotY = (row: number) => PAD + row * GAP;

interface EdgePoints {
	x1: number;
	y1: number;
	x2: number;
	y2: number;
}

function edgePoints(edge: "h" | "v", index: number): EdgePoints {
	if (edge === "h") {
		const row = Math.floor(index / DB_BOXES);
		const col = index % DB_BOXES;
		return { x1: dotX(col), y1: dotY(row), x2: dotX(col + 1), y2: dotY(row) };
	}
	const row = Math.floor(index / DB_DOTS);
	const col = index % DB_DOTS;
	return { x1: dotX(col), y1: dotY(row), x2: dotX(col), y2: dotY(row + 1) };
}

export function DotsBoxesBoard({ state, seat, onMove }: Props) {
	const myTurn = state.turn === seat && !state.result;

	const edges: { edge: "h" | "v"; index: number; owner: PlayerIndex | null }[] = [
		...Array.from({ length: DB_H_COUNT }, (_, index) => ({
			edge: "h" as const,
			index,
			owner: state.h[index],
		})),
		...Array.from({ length: DB_V_COUNT }, (_, index) => ({
			edge: "v" as const,
			index,
			owner: state.v[index],
		})),
	];

	return (
		<div className="board-frame board-md">
			{/* biome-ignore lint/a11y/useSemanticElements: an <svg> cannot be a <fieldset>. */}
			<svg
				viewBox={`0 0 ${SIZE} ${SIZE}`}
				className="w-full rounded-3xl bg-white/70 p-2 shadow-xl dark:bg-white/10"
				role="group"
				aria-label="Dots and boxes board"
			>
				<title>Dots and boxes</title>

				{state.boxes.map((owner, box) =>
					owner === null ? null : (
						<rect
							key={`box-${box}`}
							x={dotX(box % DB_BOXES)}
							y={dotY(Math.floor(box / DB_BOXES))}
							width={GAP}
							height={GAP}
							rx={12}
							className={`${BOX_COLOR[owner]} animate-pop`}
						/>
					),
				)}

				{edges.map(({ edge, index, owner }) => {
					const points = edgePoints(edge, index);
					const key = `${edge}-${index}`;
					if (owner !== null) {
						const isLast = state.lastEdge?.kind === edge && state.lastEdge.index === index;
						return (
							<line
								key={key}
								{...points}
								strokeWidth={12}
								strokeLinecap="round"
								className={`${LINE_COLOR[owner]} ${isLast ? "animate-pop" : ""}`}
							/>
						);
					}
					return (
						<g key={key}>
							<line
								{...points}
								strokeWidth={4}
								strokeLinecap="round"
								strokeDasharray="6 10"
								className="stroke-indigo-900/20 dark:stroke-indigo-100/20"
							/>
							{myTurn && (
								// A rect, not a fat line: a horizontal line has no height to
								// aim at, and small fingers need a real target.
								// biome-ignore lint/a11y/useSemanticElements: SVG has no <button>; this rect is the tap target.
								<rect
									x={Math.min(points.x1, points.x2) - HIT / 2}
									y={Math.min(points.y1, points.y2) - HIT / 2}
									width={Math.abs(points.x2 - points.x1) + HIT}
									height={Math.abs(points.y2 - points.y1) + HIT}
									rx={HIT / 2}
									role="button"
									tabIndex={0}
									aria-label={`Draw the ${edge === "h" ? "across" : "down"} line ${index + 1}`}
									onClick={() => onMove({ edge, index })}
									onKeyDown={(event) => {
										if (event.key === "Enter" || event.key === " ") {
											event.preventDefault();
											onMove({ edge, index });
										}
									}}
									className="cursor-pointer fill-transparent transition hover:fill-amber-300/40"
								/>
							)}
						</g>
					);
				})}

				{Array.from({ length: DB_DOTS * DB_DOTS }, (_, dot) => (
					<circle
						key={`dot-${dot}`}
						cx={dotX(dot % DB_DOTS)}
						cy={dotY(Math.floor(dot / DB_DOTS))}
						r={8}
						className="fill-indigo-900/70 dark:fill-indigo-100/70"
					/>
				))}
			</svg>
			<p className="mt-3 text-center text-sm text-indigo-900/70 dark:text-indigo-100/70">
				Close a box to claim it — and go again.
			</p>
		</div>
	);
}
